import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import LoginLeftPart from "../LoginLeftPart";
import LightOnlyPage from "../LightOnlyPage";
import AnalyticsSettingsMenu from "../AnalyticsSettingsMenu";
import { authFormFieldsSx } from "../../container/Onboarding/fieldStyles";
import { secondaryOutlinedButtonSx } from "../../container/Onboarding/registrationShared";

/**
 * The shared chrome for login, signup, Check Your Email and Create Your Password,
 * so the signup -> verify -> set password path reads as one flow (FE #627).
 *
 * The page is capped at the viewport and never scrolls as a whole (ALP-2); the
 * form is anchored toward the top rather than centred (ALP-3). Below ~620px of
 * height the form pane scrolls on its own instead of the page.
 */
export function AuthPageLayout({ testId, children, after = null }) {
  return (
    <LightOnlyPage className="wrap_login_page" data-testid={testId} sx={{ height: "100vh", minHeight: 0, overflow: "hidden" }}>
      <Box
        className="login-form-pane"
        sx={{
          flex: "1 1 45%",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          minWidth: 0,
          height: "100vh",
          minHeight: 0,
          overflowY: "auto",
          px: { xs: 3, md: 5 },
          pt: { xs: 5, md: "12vh" },
          pb: 5,
          bgcolor: "#FFFFFF",
          boxSizing: "border-box",
        }}
      >
        <Box className="login-form" sx={{ width: "100%", maxWidth: 400, ...authFormFieldsSx }}>
          {children}
          <AnalyticsSettingsMenu sx={{mt: 2}} />
        </Box>
      </Box>
      <LoginLeftPart />
      {after}
    </LightOnlyPage>
  );
}

export function AuthHeader({ title, subtitle, icon = null }) {
  return (
    <>
      {icon}
      <Typography
        component="h1"
        sx={{ m: 0, mb: 1.25, fontWeight: 700, fontSize: 30, lineHeight: 1.25, letterSpacing: "-0.02em", color: "#000000" }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography component="p" sx={{ m: 0, mb: 3, fontWeight: 400, fontSize: 16, lineHeight: 1.45, color: "#8692A6" }}>
          {subtitle}
        </Typography>
      ) : null}
    </>
  );
}

export function AuthAlert({ children }) {
  return (
    <Typography
      component="p"
      role="alert"
      sx={{ minHeight: 18, m: 0, mb: 1.5, fontWeight: 600, fontSize: 13, lineHeight: 1.35, color: "#D32F2F" }}
    >
      {children}
    </Typography>
  );
}

/** "Already have an account?" divider followed by an outlined link button. */
export function AuthDividerLink({ prompt, to, children }) {
  return (
    <>
      <Box
        aria-hidden="true"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.75,
          my: 3,
          "&::before, &::after": { content: '""', flex: "1 1 auto", height: "1px", bgcolor: "rgba(88,89,91,0.16)" },
        }}
      >
        <Typography
          component="span"
          sx={{ flex: "0 0 auto", fontSize: 13, fontWeight: 500, lineHeight: 1.3, color: "#8692A6", whiteSpace: "nowrap" }}
        >
          {prompt}
        </Typography>
      </Box>
      <Button variant="outlined" fullWidth component={RouterLink} to={to} sx={secondaryOutlinedButtonSx}>
        {children}
      </Button>
    </>
  );
}
